import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Divider,
  Button,
  Tooltip,
  IconButton,
  Collapse,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import dayjs from 'dayjs';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { FaComments, FaTasks, FaUsers, FaLink } from 'react-icons/fa';
import { TALKING_POINT_STATUSES } from '../../../../constants/talkingPointStatuses';
import TalkingPointStatusSelector from '../../../common/TalkingPointStatusSelector';
import {
  ACTION_ITEM_STATUSES,
  getActionStatusTextColor,
} from './ActionItemCard';
import { meetingsApi } from '../../../../services/api/meetingsApi';
import { useAlert } from '../../../../contexts/AlertContext';
import LoadingSpinner from '../../../common/LoadingSpinner';
import { EmployeeAvatar } from '../../../common/EmployeeAvatar';
import PreviousMeetingsLeftPanel from './PreviousMeetingsLeftPanel';
import { decodeHtml } from '../../../MyActivities/MyCV/helpers/ProcessTemplate';
import { MEETING_REFERENCE_TYPES } from '../../../../constants/meetingReferenceTypes';
import {
  getRefTypeLabel,
  getRefTitle,
  getRefStatusParts,
  getActionParentGoalTitle,
  isActionRef,
  isGoalRef,
  isFeedbackRef,
  getFeedbackSessionDate,
  getRefEmployee,
  getRefRelationType,
  buildLinkedItemsSummary,
} from './linkedItemsUtils';
import { formatDateOnly, toLocalDay } from '../../../../utils/dateOnly';

// Helper function to get talking point question (same as DiscussionLeftPanel)
const getQuestion = (point) => {
  return point.talkingPoint?.question || point.question || point.systemTalkingPoint?.question;
};

const PreviousMeetingsTab = ({ meetingId }) => {
  const { showAlert } = useAlert();
  const [previousMeetings, setPreviousMeetings] = useState([]);
  const [pageInfo, setPageInfo] = useState(null);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [selectedMeetingDetails, setSelectedMeetingDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [expandedTalkingPoints, setExpandedTalkingPoints] = useState(new Set());
  const [expandedActionItems, setExpandedActionItems] = useState(new Set());
  const [expandedNotes, setExpandedNotes] = useState(new Set());

  useEffect(() => {
    fetchPreviousMeetings();
  }, [meetingId]);

  const fetchPreviousMeetings = async (cursor = null) => {
    try {
      if (cursor) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
        setPreviousMeetings([]);
      }

      const response = await meetingsApi.getPreviousMeetings(meetingId, {
        limit: 20,
        cursor,
      });

      if (cursor) {
        setPreviousMeetings(prev => [...prev, ...(response?.items || [])]);
      } else {
        setPreviousMeetings(response?.items || []);
      }
      setPageInfo(response?.pageInfo || null);
    } catch (err) {
      showAlert('Failed to load previous meetings', 'error', err);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (pageInfo?.hasNextPage && pageInfo?.endCursor) {
      fetchPreviousMeetings(pageInfo.endCursor);
    }
  };

  const handleSelectMeeting = async (meetingItem) => {
    if (selectedMeeting?.id === meetingItem.id) {
      setSelectedMeeting(null);
      setSelectedMeetingDetails(null);
      return;
    }

    setSelectedMeeting(meetingItem);
    setIsLoadingDetails(true);
    setShowFullDescription(false);
    setExpandedTalkingPoints(new Set());
    setExpandedActionItems(new Set());
    setExpandedNotes(new Set());

    try {
      const details = await meetingsApi.getMeetingById(meetingItem.id);
      setSelectedMeetingDetails(details);
    } catch (err) {
      showAlert('Failed to load meeting details', 'error', err);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const toggleTalkingPointExpanded = (pointId) => {
    setExpandedTalkingPoints(prev => {
      const next = new Set(prev);
      if (next.has(pointId)) {
        next.delete(pointId);
      } else {
        next.add(pointId);
      }
      return next;
    });
  };

  const toggleActionItemExpanded = (itemId) => {
    setExpandedActionItems(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  const toggleNoteExpanded = (noteId) => {
    setExpandedNotes(prev => {
      const next = new Set(prev);
      if (next.has(noteId)) {
        next.delete(noteId);
      } else {
        next.add(noteId);
      }
      return next;
    });
  };

  // Helper to check if action item is still open/pending
  const isActionItemOpen = (item) => {
    return item.status === ACTION_ITEM_STATUSES.OPEN || item.status === ACTION_ITEM_STATUSES.IN_PROGRESS;
  };

  // Helper to check if action item is overdue
  const isActionItemOverdue = (item) => {
    if (!item.deadlineAt || !isActionItemOpen(item)) return false;
    return toLocalDay(item.deadlineAt)?.isBefore(dayjs(), 'day');
  };

  // Get linked references from meeting details (filtered by type)
  const getLinkedReferences = () => {
    if (!selectedMeetingDetails?.references) {
      return [];
    }
    return selectedMeetingDetails.references.filter(r =>
      r.refType === MEETING_REFERENCE_TYPES.IDP_GOAL ||
      r.refType === MEETING_REFERENCE_TYPES.IDP_ACTION ||
      r.refType === MEETING_REFERENCE_TYPES.PERFORMANCE_FEEDBACK_RESULT
    );
  };

  // Get meeting-level references (no talkingPointId)
  const getMeetingLevelReferences = () => {
    return getLinkedReferences().filter(r => !r.meetingTalkingPointId);
  };

  // Inline component for lite linked items display
  const LinkedItemsList = ({ references, title }) => {
    if (!references || references.length === 0) {
      return null;
    }

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          <FaLink style={{ marginRight: 8 }} />
          {title} ({references.length})
        </Typography>
        {references.map(ref => {
          const isAction = isActionRef(ref);
          const isGoal = isGoalRef(ref);
          const isFeedback = isFeedbackRef(ref);
          const parentGoalTitle = getActionParentGoalTitle(ref);
          const employee = getRefEmployee(ref);
          const sessionDate = getFeedbackSessionDate(ref);
          const relationType = getRefRelationType(ref);
          const { progress, isOverdue } = getRefStatusParts(ref);

          return (
            <Box key={ref.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.25, ml: 2 }}>
              <Typography variant="body2" color="text.secondary">•</Typography>
              <Typography variant="body2" color="text.secondary">
                {getRefTypeLabel(ref)}: {getRefTitle(ref)}
                {isAction && parentGoalTitle && ` part of ${parentGoalTitle}`}
                {progress != null && ` (${progress}%`}
                {progress != null && isOverdue && ', '}
                {progress == null && isOverdue && ' ('}
              </Typography>
              {isOverdue && (
                <Typography variant="body2" sx={{ color: 'error.main' }}>
                  overdue
                </Typography>
              )}
              {(progress != null || isOverdue) && (
                <Typography variant="body2" color="text.secondary">)</Typography>
              )}
              {isFeedback && sessionDate && (
                <Typography variant="body2" color="text.secondary"> · {sessionDate}</Typography>
              )}
              {isFeedback && relationType && (
                <Typography variant="body2" color="text.secondary"> · {relationType.name}</Typography>
              )}
              {(isFeedback || isGoal) && employee && (
                <>
                  <Typography variant="body2" color="text.secondary">·</Typography>
                  <Tooltip title={employee.fullName}>
                    <Box sx={{ flexShrink: 0 }}>
                      <EmployeeAvatar employee={employee} size={18} />
                    </Box>
                  </Tooltip>
                </>
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', gap: 3, height: '100%', minHeight: 500 }}>
      {/* Left Panel - Meeting List */}
      <PreviousMeetingsLeftPanel
        isLoading={isLoading}
        previousMeetings={previousMeetings}
        selectedMeeting={selectedMeeting}
        onSelectMeeting={handleSelectMeeting}
        pageInfo={pageInfo}
        isLoadingMore={isLoadingMore}
        onLoadMore={handleLoadMore}
      />

      {/* Right Panel - Meeting Details */}
      <Paper
        sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {!selectedMeeting ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              color: 'text.secondary',
            }}
          >
            <FaComments style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }} />
            <Typography variant="body1">
              Select a meeting to view its details
            </Typography>
          </Box>
        ) : isLoadingDetails ? (
          <LoadingSpinner />
        ) : selectedMeetingDetails ? (
          <Box sx={{ p: 3, overflow: 'auto' }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
                <Typography variant="h5">
                  {selectedMeetingDetails.subject}
                </Typography>
                {selectedMeetingDetails.currentStatus && (
                  <>
                    <Typography variant="h5" sx={{ color: '#9CA3AF' }}>
                      ·
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        color: selectedMeetingDetails.currentStatus.color || '#6B7280',
                      }}
                    >
                      {selectedMeetingDetails.currentStatus.name}
                    </Typography>
                  </>
                )}
              </Box>
              <Typography variant="body2" color="text.secondary">
                {selectedMeetingDetails.scheduledAt
                  ? dayjs(selectedMeetingDetails.scheduledAt).format('DD/MM/YYYY')
                  : 'Not scheduled'}
              </Typography>
              {(() => {
                const overdueCount = selectedMeetingDetails.actionItems?.filter(item =>
                  isActionItemOpen(item) &&
                  item.deadlineAt &&
                  toLocalDay(item.deadlineAt)?.isBefore(dayjs(), 'day')
                ).length || 0;
                if (overdueCount === 0) return null;
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <WarningAmberIcon sx={{ fontSize: 16, color: '#DC2626' }} />
                    <Typography variant="body2" sx={{ color: '#DC2626', fontWeight: 500 }}>
                      {overdueCount} overdue action{overdueCount > 1 ? 's' : ''}
                    </Typography>
                  </Box>
                );
              })()}
            </Box>

            {/* Description */}
            {selectedMeetingDetails.description && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Description
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: showFullDescription ? 'unset' : 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {selectedMeetingDetails.description}
                </Typography>
                <Button
                  size="small"
                  onClick={() => setShowFullDescription(!showFullDescription)}
                  sx={{ mt: 0.5, p: 0, minWidth: 'auto', textTransform: 'none' }}
                >
                  {showFullDescription ? 'Show less' : 'Show more'}
                </Button>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Participants */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  <FaUsers style={{ marginRight: 8 }} />
                  Participants ({selectedMeetingDetails.attendees?.length || 0})
                </Typography>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  '& > *': {
                    marginLeft: '-8px',
                    border: '2px solid white',
                    borderRadius: '50%',
                  },
                  '& > *:first-of-type': {
                    marginLeft: 0,
                  },
                }}>
                  {selectedMeetingDetails.attendees?.slice(0, 5).map((attendee) => (
                    <Tooltip
                      key={attendee.id}
                      title={`${attendee.employee?.fullName} (${attendee.attendeeType?.name || 'Participant'})`}
                    >
                      <Box>
                        <EmployeeAvatar
                          employee={attendee.employee}
                          size={28}
                        />
                      </Box>
                    </Tooltip>
                  ))}
                  {selectedMeetingDetails.attendees?.length > 5 && (
                    <Tooltip title={`${selectedMeetingDetails.attendees.length - 5} more participants`}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          backgroundColor: '#E5E7EB',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '11px',
                          fontWeight: 600,
                          color: '#6B7280',
                        }}
                      >
                        +{selectedMeetingDetails.attendees.length - 5}
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Meeting-Level Linked Items */}
            {getMeetingLevelReferences().length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <LinkedItemsList
                  references={getMeetingLevelReferences()}
                  title="Related to this meeting"
                />
              </>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Talking Points */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                <FaComments style={{ marginRight: 8 }} />
                Talking Points ({selectedMeetingDetails.talkingPoints?.length || 0})
              </Typography>
              {selectedMeetingDetails.talkingPoints?.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {selectedMeetingDetails.talkingPoints.map((point) => {
                    const isExpanded = expandedTalkingPoints.has(point.id);
                    // Get notes for this talking point from the separate notes array
                    const pointNotes = selectedMeetingDetails.notes?.filter(
                      note => note.meetingTalkingPointId === point.id
                    ) || [];
                    // Get linked references from the talking point itself
                    const pointReferences = point.references?.filter(r =>
                      r.refType === MEETING_REFERENCE_TYPES.IDP_GOAL ||
                      r.refType === MEETING_REFERENCE_TYPES.IDP_ACTION ||
                      r.refType === MEETING_REFERENCE_TYPES.PERFORMANCE_FEEDBACK_RESULT
                    ) || [];
                    const hasContent = pointNotes.length > 0 || pointReferences.length > 0;
                    return (
                      <Box
                        key={point.id}
                        sx={{
                          borderBottom: '1px solid #F1F5F9',
                          py: 1,
                          '&:last-child': { borderBottom: 'none' },
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: hasContent ? 'pointer' : 'default',
                          }}
                          onClick={() => hasContent && toggleTalkingPointExpanded(point.id)}
                        >
                          {hasContent ? (
                            <IconButton size="small" sx={{ p: 0 }}>
                              {isExpanded ? (
                                <KeyboardArrowDownIcon sx={{ fontSize: 18, color: '#6B7280' }} />
                              ) : (
                                <KeyboardArrowRightIcon sx={{ fontSize: 18, color: '#6B7280' }} />
                              )}
                            </IconButton>
                          ) : (
                            <Box sx={{ width: 18 }} />
                          )}
                          <TalkingPointStatusSelector
                            status={point.status || TALKING_POINT_STATUSES.PENDING}
                            onChange={() => {}}
                            disabled
                          />
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {getQuestion(point)}
                          </Typography>
                          {hasContent && (
                            <Typography variant="caption" color="text.secondary">
                              {pointNotes.length > 0 && `${pointNotes.length} note${pointNotes.length !== 1 ? 's' : ''}`}
                              {pointNotes.length > 0 && pointReferences.length > 0 && ' · '}
                              {buildLinkedItemsSummary(pointReferences, false)}
                            </Typography>
                          )}
                        </Box>
                        <Collapse in={isExpanded}>
                          <Box sx={{ mt: 1, ml: 3.5, pl: 2, borderLeft: '2px solid #E2E8F0' }}>
                            {pointNotes.map((note, index) => (
                              <Box
                                key={note.id}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: 1,
                                  py: 1,
                                  borderBottom: index < pointNotes.length - 1 ? '1px solid #F1F5F9' : 'none',
                                }}
                              >
                                <Tooltip title={note.addedByEmployee?.fullName || 'Unknown'}>
                                  <Box sx={{ flexShrink: 0 }}>
                                    <EmployeeAvatar
                                      employee={note.addedByEmployee}
                                      size={24}
                                    />
                                  </Box>
                                </Tooltip>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  component="div"
                                  sx={{
                                    flex: 1,
                                    '& p': { margin: 0 },
                                    '& ul, & ol': { margin: 0, paddingLeft: 2.5 },
                                  }}
                                  dangerouslySetInnerHTML={{ __html: decodeHtml(note.notes || '') }}
                                />
                              </Box>
                            ))}
                            {/* Linked Development Items for this talking point */}
                            <LinkedItemsList
                              references={pointReferences}
                              title="Linked Development Items"
                            />
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No talking points recorded
                </Typography>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Action Items */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                <FaTasks style={{ marginRight: 8 }} />
                Action Items ({selectedMeetingDetails.actionItems?.length || 0})
              </Typography>
              {selectedMeetingDetails.actionItems?.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {selectedMeetingDetails.actionItems.map((item) => {
                    const isExpanded = expandedActionItems.has(item.id);
                    const isOpen = isActionItemOpen(item);
                    const isOverdue = isActionItemOverdue(item);
                    const hasDescription = item.description && item.description.trim().length > 0;

                    return (
                      <Box
                        key={item.id}
                        sx={{
                          borderBottom: '1px solid #F1F5F9',
                          py: 1,
                          backgroundColor: isOpen ? (isOverdue ? 'rgba(220, 38, 38, 0.05)' : 'rgba(251, 191, 36, 0.08)') : 'transparent',
                          px: 1,
                          mx: -1,
                          borderRadius: 1,
                          '&:last-child': { borderBottom: 'none' },
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: 'pointer',
                          }}
                          onClick={() => toggleActionItemExpanded(item.id)}
                        >
                          <IconButton size="small" sx={{ p: 0 }}>
                            {isExpanded ? (
                              <KeyboardArrowDownIcon sx={{ fontSize: 18, color: '#6B7280' }} />
                            ) : (
                              <KeyboardArrowRightIcon sx={{ fontSize: 18, color: '#6B7280' }} />
                            )}
                          </IconButton>
                          {item.ownerEmployee && (
                            <Tooltip title={item.ownerEmployee.fullName}>
                              <Box sx={{ flexShrink: 0 }}>
                                <EmployeeAvatar employee={item.ownerEmployee} size={20} />
                              </Box>
                            </Tooltip>
                          )}
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {item.title}
                          </Typography>
                        </Box>

                        {/* Action Item Meta Info */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 4.5, mt: 0.5 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: getActionStatusTextColor(item.status),
                              fontWeight: 500,
                            }}
                          >
                            {item.status === ACTION_ITEM_STATUSES.IN_PROGRESS ? 'In Progress' : item.status}
                          </Typography>
                          {item.deadlineAt && (
                            <>
                              <Typography variant="caption" color="text.secondary">·</Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: isOverdue ? '#DC2626' : '#6B7280',
                                  fontWeight: isOverdue ? 400 : 400,
                                }}
                              >
                                {isOverdue ? 'Overdue: ' : 'Due: '}
                                {formatDateOnly(item.deadlineAt, 'DD/MM/YYYY')}
                              </Typography>
                            </>
                          )}
                        </Box>

                        {/* Expandable Description */}
                        <Collapse in={isExpanded}>
                          <Box sx={{ mt: 1, ml: 4.5, pl: 2, borderLeft: '2px solid #E2E8F0' }}>
                            {hasDescription ? (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                component="div"
                                sx={{
                                  '& p': { margin: 0 },
                                  '& ul, & ol': { margin: 0, paddingLeft: 2.5 },
                                }}
                                dangerouslySetInnerHTML={{ __html: decodeHtml(item.description) }}
                              />
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                No description
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No action items recorded
                </Typography>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* General Notes */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <DescriptionOutlinedIcon sx={{ fontSize: 16, mr: 1 }} />
                General Notes ({selectedMeetingDetails.notes?.filter(n => !n.meetingTalkingPointId).length || 0})
              </Typography>
              {selectedMeetingDetails.notes?.filter(n => !n.meetingTalkingPointId).length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {selectedMeetingDetails.notes
                    .filter(n => !n.meetingTalkingPointId)
                    .map((note) => {
                      const isExpanded = expandedNotes.has(note.id);
                      const hasContent = note.notes && note.notes.trim().length > 0;

                      return (
                        <Box
                          key={note.id}
                          sx={{
                            borderBottom: '1px solid #F1F5F9',
                            py: 1,
                            '&:last-child': { borderBottom: 'none' },
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              cursor: 'pointer',
                            }}
                            onClick={() => toggleNoteExpanded(note.id)}
                          >
                            <IconButton size="small" sx={{ p: 0 }}>
                              {isExpanded ? (
                                <KeyboardArrowDownIcon sx={{ fontSize: 18, color: '#6B7280' }} />
                              ) : (
                                <KeyboardArrowRightIcon sx={{ fontSize: 18, color: '#6B7280' }} />
                              )}
                            </IconButton>
                            {note.addedByEmployee && (
                              <Tooltip title={note.addedByEmployee.fullName}>
                                <Box sx={{ flexShrink: 0 }}>
                                  <EmployeeAvatar employee={note.addedByEmployee} size={20} />
                                </Box>
                              </Tooltip>
                            )}
                            <Typography variant="body2" sx={{ flex: 1 }}>
                              {dayjs(note.createdAt).format('DD/MM/YYYY')}
                            </Typography>
                          </Box>

                          {/* Expandable Note Content */}
                          <Collapse in={isExpanded}>
                            <Box sx={{ mt: 1, ml: 4.5, pl: 2, borderLeft: '2px solid #E2E8F0' }}>
                              {hasContent ? (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  component="div"
                                  sx={{
                                    '& p': { margin: 0 },
                                    '& ul, & ol': { margin: 0, paddingLeft: 2.5 },
                                  }}
                                  dangerouslySetInnerHTML={{ __html: decodeHtml(note.notes) }}
                                />
                              ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                  No content
                                </Typography>
                              )}
                            </Box>
                          </Collapse>
                        </Box>
                      );
                    })}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No general notes recorded
                </Typography>
              )}
            </Box>
          </Box>
        ) : null}
      </Paper>
    </Box>
  );
};

export default PreviousMeetingsTab;
